import { zodResolver } from '@hookform/resolvers/zod';
import { ChevronDown, UserPlus, X } from 'lucide-react';
import React, { forwardRef, useImperativeHandle, useMemo } from 'react';
import { type Control, useFieldArray, useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@apps/web/components/ui/button';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '@apps/web/components/ui/collapsible';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@apps/web/components/ui/form';
import { Input } from '@apps/web/components/ui/input';
import { MultiSelect } from '@apps/web/components/ui/multi_select';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import { getRoleDisplayName } from '@apps/web/lib/caregiver_role_utils';
import { CAREGIVER_ROLES } from '@libraries/typescript/api_client';

const DEFAULT_CAREGIVER_FORM_VALUES = {
    name: '',
    phone_number: '',
    address_line_1: '',
    address_line_2: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
    roles: [],
};

const caregiverSchema = z.object({
    name: z.string().min(1, 'Caregiver name is required'),
    phone_number: z
        .string()
        .regex(/^\+?1?\d{10,14}$/, 'Please enter a valid US phone number'),
    // Optional OrgCaregiverRecord fields
    address_line_1: z.string().optional(),
    address_line_2: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    postal_code: z.string().optional(),
    country: z.string().optional(),
    roles: z.array(z.enum(CAREGIVER_ROLES)).optional(),
});

const baseFormSchema = z.object({
    caregivers: z.array(caregiverSchema).min(1, 'Add at least one caregiver'),
});

type FormValues = z.infer<typeof baseFormSchema>;

export type CreateCaregiverFormProps = {
    readonly onComplete?: (
        caregivers: Array<{ id: string; name: string }>,
    ) => void;
    readonly onCancel?: () => void;
    readonly maxCaregivers?: number;
    readonly hideActions?: boolean;
};

export type CreateCaregiverFormHandle = {
    readonly submit: () => Promise<Array<{ id: string; name: string }>>;
};

export const CreateCaregiverForm = forwardRef<
    CreateCaregiverFormHandle,
    CreateCaregiverFormProps
>(function CreateTestCaregiverForm(
    { onComplete, onCancel, maxCaregivers = 10, hideActions = false },
    ref,
) {
    const organizationId = useOrganizationId();

    const schemaWithLimit = useMemo(
        () =>
            z.object({
                caregivers: z
                    .array(caregiverSchema)
                    .min(1, 'Add at least one caregiver')
                    .max(
                        maxCaregivers,
                        `Add at most ${maxCaregivers} caregiver${
                            maxCaregivers === 1 ? '' : 's'
                        }`,
                    ),
            }),
        [maxCaregivers],
    );

    const form = useForm<FormValues>({
        resolver: zodResolver(schemaWithLimit),
        defaultValues: {
            caregivers: [DEFAULT_CAREGIVER_FORM_VALUES],
        },
        mode: 'onChange',
    });

    const { fields, append, remove } = useFieldArray({
        control: form.control,
        name: 'caregivers',
    });

    const createCaregivers = async (values: FormValues) => {
        if (!organizationId) {
            toast.error('Cannot create caregivers', {
                persistNotification: true,
                notificationType: 'form_failure',
            });
            return [] as Array<{ id: string; name: string }>;
        }

        try {
            const created: Array<{ id: string; name: string }> = [];

            for (const caregiver of values.caregivers) {
                try {
                    const body = {
                        name: caregiver.name.trim(),
                        phone_number: caregiver.phone_number.trim(),
                        address_line_1: caregiver.address_line_1?.trim(),
                        address_line_2: caregiver.address_line_2?.trim(),
                        city: caregiver.city?.trim(),
                        state: caregiver.state?.trim(),
                        postal_code: caregiver.postal_code?.trim(),
                        country: caregiver.country?.trim(),
                        roles: caregiver.roles,
                    };

                    const response = await getApiClient().post({
                        url: '/organizations/{organization_id}/caregivers/with-contact',
                        pathParams: { organization_id: organizationId },
                        body,
                    });

                    // Only expose id and name to parent
                    created.push({ id: response.id, name: response.name });
                } catch (err) {
                    console.error('Failed to create caregiver:', err);
                    toast.error(`Failed to create ${caregiver.name}`, {
                        persistNotification: false,
                    });
                }
            }

            if (created.length > 0) {
                toast.success(
                    `Created ${created.length} caregiver${
                        created.length === 1 ? '' : 's'
                    }`,
                    { persistNotification: false },
                );
                onComplete?.(created);
            }

            return created;
        } catch (error) {
            toast.error(error.message || 'Failed to add caregiver', {
                persistNotification: false,
            });
            console.error(error);
            return [];
        }
    };

    const onSubmit = async (values: FormValues) => {
        const created = await createCaregivers(values);
        if (created.length > 0 && !hideActions) {
            onCancel?.();
        }
    };
    const isSubmitDisabled =
        form.formState.isSubmitting || !form.formState.isValid;

    useImperativeHandle(ref, () => ({
        submit: () =>
            new Promise<Array<{ id: string; name: string }>>((resolve) => {
                const handler = form.handleSubmit(
                    async (values) => {
                        const result = await createCaregivers(values);
                        resolve(result);
                    },
                    // invalid
                    () => resolve([]),
                );
                handler();
            }),
    }));

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-2">
                <div className="flex items-center justify-between">
                    <FormLabel>Caregivers</FormLabel>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => append(DEFAULT_CAREGIVER_FORM_VALUES)}
                        disabled={
                            fields.length >= maxCaregivers ||
                            form.formState.isSubmitting
                        }
                    >
                        <UserPlus className="h-4 w-4" />
                    </Button>
                </div>
                <p className="text-muted-foreground text-xs">
                    Fields marked with * are required.
                </p>
                <div className="max-h-[500px] space-y-3 overflow-y-auto">
                    {fields.map((field, index) => (
                        <CaregiverItem
                            key={field.id}
                            control={form.control}
                            index={index}
                            canRemove={fields.length > 1}
                            isSubmitting={form.formState.isSubmitting}
                            onRemove={() => remove(index)}
                        />
                    ))}
                </div>
                {!hideActions && (
                    <div className="flex items-center justify-end gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                                onCancel?.();
                            }}
                            disabled={form.formState.isSubmitting}
                        >
                            Cancel
                        </Button>
                        <Button type="submit" disabled={isSubmitDisabled}>
                            {form.formState.isSubmitting
                                ? 'Creating…'
                                : 'Create'}
                        </Button>
                    </div>
                )}
            </form>
        </Form>
    );
});

// Subcomponents for readability
type CaregiverItemProps = {
    readonly control: Control<FormValues>;
    readonly index: number;
    readonly canRemove: boolean;
    readonly isSubmitting: boolean;
    readonly onRemove: () => void;
};

function CaregiverItem({
    control,
    index,
    canRemove,
    isSubmitting,
    onRemove,
}: CaregiverItemProps) {
    return (
        <div className="rounded-md border p-3">
            <div className="flex items-start gap-3">
                <div className="flex-1 space-y-3">
                    <BaseCaregiverFields control={control} index={index} />
                    <AddressDetailsSection control={control} index={index} />
                    <RolesSection control={control} index={index} />
                </div>
                {canRemove && (
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={onRemove}
                        disabled={isSubmitting}
                        title="Remove caregiver"
                    >
                        <X className="h-4 w-4" />
                    </Button>
                )}
            </div>
        </div>
    );
}

function BaseCaregiverFields({
    control,
    index,
}: {
    readonly control: Control<FormValues>;
    readonly index: number;
}) {
    return (
        <React.Fragment>
            <FormField
                control={control}
                name={`caregivers.${index}.name` as const}
                render={({ field }) => (
                    <FormItem>
                        <FormLabel>Name *</FormLabel>
                        <FormControl>
                            <Input
                                placeholder={`Caregiver ${index + 1}`}
                                {...field}
                            />
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                )}
            />
            <FormField
                control={control}
                name={`caregivers.${index}.phone_number` as const}
                render={({ field }) => (
                    <FormItem>
                        <FormLabel>Phone Number *</FormLabel>
                        <FormControl>
                            <Input
                                type="tel"
                                placeholder="(555) 123-4567"
                                {...field}
                            />
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                )}
            />
        </React.Fragment>
    );
}

function AddressDetailsSection({
    control,
    index,
}: {
    readonly control: Control<FormValues>;
    readonly index: number;
}) {
    return (
        <Collapsible defaultOpen={false}>
            <CollapsibleTrigger className="bg-muted/30 hover:bg-muted/50 text-foreground group flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm font-medium">
                <span>Address details</span>
                <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]:rotate-180" />
            </CollapsibleTrigger>
            <CollapsibleContent>
                <div className="mt-3 space-y-3">
                    <FormField
                        control={control}
                        name={`caregivers.${index}.address_line_1` as const}
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Address Line 1</FormLabel>
                                <FormControl>
                                    <Input
                                        placeholder="Street address"
                                        {...field}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={control}
                        name={`caregivers.${index}.address_line_2` as const}
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Address Line 2</FormLabel>
                                <FormControl>
                                    <Input
                                        placeholder="Apt, suite, etc."
                                        {...field}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <div className="grid grid-cols-3 gap-3">
                        <FormField
                            control={control}
                            name={`caregivers.${index}.city` as const}
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>City</FormLabel>
                                    <FormControl>
                                        <Input placeholder="City" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={control}
                            name={`caregivers.${index}.state` as const}
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>State</FormLabel>
                                    <FormControl>
                                        <Input placeholder="State" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={control}
                            name={`caregivers.${index}.postal_code` as const}
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Postal Code</FormLabel>
                                    <FormControl>
                                        <Input
                                            placeholder="ZIP / Postal"
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                    <FormField
                        control={control}
                        name={`caregivers.${index}.country` as const}
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Country</FormLabel>
                                <FormControl>
                                    <Input placeholder="US" {...field} />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <p className="text-muted-foreground text-xs">
                        If you provide one address field, fill out all address
                        fields.
                    </p>
                </div>
            </CollapsibleContent>
        </Collapsible>
    );
}

function RolesSection({
    control,
    index,
}: {
    readonly control: Control<FormValues>;
    readonly index: number;
}) {
    return (
        <FormField
            control={control}
            name={`caregivers.${index}.roles` as const}
            render={({ field }) => (
                <FormItem>
                    <FormLabel>Roles</FormLabel>
                    <FormControl>
                        <MultiSelect
                            options={CAREGIVER_ROLES.map((role) => ({
                                value: role,
                                label: getRoleDisplayName(role),
                            }))}
                            selected={
                                (field.value as unknown as Array<string>) || []
                            }
                            onChange={(values) =>
                                field.onChange(
                                    values as unknown as typeof field.value,
                                )
                            }
                            placeholder="Select roles"
                        />
                    </FormControl>
                    <FormMessage />
                </FormItem>
            )}
        />
    );
}
